import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { usePartyFilter } from '../context/PartyFilterContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { formatEuro, formatSqm, getAssetClassLabel } from '../utils/formatters';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Building2, Users, AlertTriangle, ChevronRight, X, Leaf, ExternalLink, HelpCircle, Upload, FileText, Loader2, RefreshCw, Search, Zap, Eye } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import { BuildingDetailDialog } from '../components/building/BuildingDetailDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BAG_VIEWER_URL = 'https://bagviewer.kadaster.nl/lvbag/bag-viewer/?zoomlevel=1';

const ENERGY_LABELS = ['A++++', 'A+++', 'A++', 'A+', 'A', 'B', 'C', 'D', 'E', 'F', 'G'];

// BAG Lookup Button Component
const BagLookupButton = () => (
  <a 
    href={BAG_VIEWER_URL}
    target="_blank"
    rel="noopener noreferrer"
    className="inline-flex items-center gap-1 px-2 py-1 text-xs text-[#b4976f] hover:text-[#c8b599] bg-[#264162]/30 hover:bg-[#264162]/50 rounded transition-colors"
    title="Open BAG Viewer om BAG IDs op te zoeken"
  >
    <HelpCircle className="h-3 w-3" />
    BAG?
    <ExternalLink className="h-3 w-3" />
  </a>
);

// Market standard energy label colors: Green (A) to Red (G)
const ENERGY_LABEL_COLORS = {
  'A++++': '#009036',
  'A+++': '#009036',
  'A++': '#009036',
  'A+': '#52b946',
  'A': '#c8d300',
  'B': '#fff200',
  'C': '#edb731',
  'D': '#d66f1e',
  'E': '#cc232a',
  'F': '#cc232a',
  'G': '#cc232a',
  'N/A': '#64748b'
};

const assetClasses = [
  { value: 'residential', label: 'residential' },
  { value: 'office', label: 'office' },
  { value: 'retail', label: 'retail' },
  { value: 'light_industrial', label: 'lightIndustrial' },
  { value: 'combination', label: 'combination' },
  { value: 'logistics', label: 'logistics' },
  { value: 'other', label: 'other' }
];

const initialPropertyForm = {
  address: '',
  street: '',
  house_number: '',
  house_number_supplement: '',
  postal_code: '',
  city: '',
  bag_pand_ids: [],
  bag_vbo_ids: [],
  energy_labels: [],
  market_value: '',
  total_sqm_bvo: '',
  total_sqm_vvo: '',
  asset_class: 'residential',
  capex_percentage: '',
  image_url: '',
  notes: '',
  owners: []
};

const initialTenantForm = {
  realestate_id: '',
  bag_vbo_id: '',
  tenant_name: '',
  chamber_of_commerce_number: '',
  start_date: '',
  end_date: '',
  option_periods: '',
  rental_amount: '',
  incentives: '',
  sqm_rented: '',
  is_vacancy: false,
  contract_url: '',
  contract_type: ''
};

export const RealEstatePage = () => {
  const { canEdit, canDelete } = useAuth();
  const { t, language } = useLanguage();
  const { selectedPartyId } = usePartyFilter();
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [propertyDialogOpen, setPropertyDialogOpen] = useState(false);
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteType, setDeleteType] = useState('property');
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [viewingProperty, setViewingProperty] = useState(null);
  const [propertyForm, setPropertyForm] = useState(initialPropertyForm);
  const [tenantForm, setTenantForm] = useState(initialTenantForm);
  const [saving, setSaving] = useState(false);
  
  // Building Detail Dialog state (VT15 Gebouwenpaspoort)
  const [buildingDetailOpen, setBuildingDetailOpen] = useState(false);
  const [selectedBuildingForDetail, setSelectedBuildingForDetail] = useState(null);
  
  // Individual input states for BAG IDs
  const [newBagPandId, setNewBagPandId] = useState('');
  const [newBagVboId, setNewBagVboId] = useState('');
  
  // Energy label state - VBO ID is now selected from dropdown
  const [energyLabelInput, setEnergyLabelInput] = useState({ bag_vbo_id: '', energy_label: 'A' });
  const [ownerInput, setOwnerInput] = useState({ party_id: '', ownership_percentage: '' });
  
  // Contract upload and OCR state
  const [extractingContract, setExtractingContract] = useState(false);
  const [uploadingContract, setUploadingContract] = useState(false);
  const contractFileRef = useRef(null);
  
  // Energy label auto-fetch state
  const [fetchingEnergyLabel, setFetchingEnergyLabel] = useState(false);
  const [energyLabelResults, setEnergyLabelResults] = useState([]);

  // Handler to open Building Detail Dialog
  const handleOpenBuildingDetail = (property) => {
    setSelectedBuildingForDetail(property);
    setBuildingDetailOpen(true);
  };

  // Handler when building is saved from detail dialog
  const handleBuildingDetailSave = (updatedBuilding) => {
    setProperties(prev => prev.map(p => 
      p.realestate_id === updatedBuilding.realestate_id ? { ...p, ...updatedBuilding } : p
    ));
  };

  useEffect(() => {
    fetchData();
  }, [selectedPartyId]);

  // Fetch energy labels from overheid.io API
  const fetchEnergyLabels = async () => {
    // Determine what to search by
    const bagPandId = propertyForm.bag_pand_ids?.[0];
    const bagVboId = propertyForm.bag_vbo_ids?.[0];
    const postcode = propertyForm.postal_code;
    const huisnummer = propertyForm.house_number;
    
    if (!bagPandId && !bagVboId && !(postcode && huisnummer)) {
      toast.error('Voeg eerst een BAG Pand ID, BAG VBO ID, of postcode+huisnummer toe om energielabels op te halen.');
      return;
    }
    
    setFetchingEnergyLabel(true);
    setEnergyLabelResults([]);
    
    try {
      const params = new URLSearchParams();
      if (bagVboId) {
        // Priority 1: BAG VBO ID (direct lookup)
        params.append('bag_vbo_id', bagVboId);
      } else if (postcode && huisnummer) {
        // Priority 2: Address lookup
        params.append('postcode', postcode);
        params.append('huisnummer', huisnummer);
        // EP-Online uses 'huisletter' and 'huisnummertoevoeging'
        if (propertyForm.house_number_supplement) {
          // Check if it's a letter (A, B, etc.) or number addition
          const supplement = propertyForm.house_number_supplement.trim();
          if (/^[A-Za-z]$/.test(supplement)) {
            params.append('huisletter', supplement.toUpperCase());
          } else {
            params.append('huisnummertoevoeging', supplement);
          }
        }
        if (bagPandId) {
          params.append('bag_pand_id', bagPandId);
        }
      } else if (bagPandId && postcode && huisnummer) {
        // Priority 3: BAG Pand ID needs address for EP-Online
        params.append('bag_pand_id', bagPandId);
        params.append('postcode', postcode);
        params.append('huisnummer', huisnummer);
      } else {
        toast.error('Voeg eerst een BAG VBO ID of postcode+huisnummer toe.');
        setFetchingEnergyLabel(false);
        return;
      }
      
      const response = await axios.get(`${API}/energylabels/fetch?${params.toString()}`, { withCredentials: true });
      
      if (response.data.success && response.data.labels?.length > 0) {
        setEnergyLabelResults(response.data.labels);
        toast.success(response.data.message);
        
        // Auto-apply first result if only one found
        if (response.data.labels.length === 1) {
          applyEnergyLabel(response.data.labels[0]);
        }
      } else {
        toast.info(response.data.message || 'Geen energielabels gevonden.');
      }
    } catch (error) {
      console.error('Error fetching energy labels:', error);
      toast.error('Fout bij ophalen energielabels. Controleer of EP_ONLINE_API_KEY is geconfigureerd.');
    } finally {
      setFetchingEnergyLabel(false);
    }
  };

  // Apply selected energy label to form
  const applyEnergyLabel = (labelData) => {
    const newLabel = {
      bag_vbo_id: labelData.bag_vbo_id || propertyForm.bag_vbo_ids?.[0] || 'auto',
      energy_label: labelData.energielabel_klasse
    };
    
    // Check if this VBO already has a label
    const existingIndex = propertyForm.energy_labels.findIndex(
      el => el.bag_vbo_id === newLabel.bag_vbo_id
    );
    
    if (existingIndex >= 0) {
      // Update existing
      const updatedLabels = [...propertyForm.energy_labels];
      updatedLabels[existingIndex] = newLabel;
      setPropertyForm({ ...propertyForm, energy_labels: updatedLabels });
    } else {
      // Add new
      setPropertyForm({
        ...propertyForm,
        energy_labels: [...propertyForm.energy_labels, newLabel]
      });
    }
    
    toast.success(`Energielabel ${labelData.energielabel_klasse} toegepast`);
    setEnergyLabelResults([]);
  };

  const fetchData = async () => {
    try {
      const params = selectedPartyId !== 'all' ? `?party_id=${selectedPartyId}` : '';
      const [propertiesRes, huurdersRes, partiesRes] = await Promise.all([
        axios.get(`${API}/realestate${params}`, { withCredentials: true }),
        axios.get(`${API}/huurders`, { withCredentials: true }),
        axios.get(`${API}/parties`, { withCredentials: true })
      ]);
      setProperties(propertiesRes.data);
      // Map huurders to tenant format for backwards compatibility in UI
      const mappedTenants = huurdersRes.data.map(h => ({
        ...h,
        tenant_id: h.huurder_id,
        tenant_name: h.tenant_naam,
        rental_amount: h.huurprijs_per_jaar,
        sqm_rented: h.gehuurde_oppervlakte_m2,
        start_date: h.startdatum,
        end_date: h.einddatum,
        rent_per_sqm: h.huurprijs_per_m2,
        is_vacancy: h.is_leegstand,
        source: 'unified'
      }));
      setTenants(mappedTenants);
      setParties(partiesRes.data);
    } catch (error) {
      toast.error(t('errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  // Property handlers
  const handleOpenPropertyDialog = (property = null) => {
    if (property) {
      setSelectedProperty(property);
      setPropertyForm({
        address: property.address,
        street: property.street,
        house_number: property.house_number,
        house_number_supplement: property.house_number_supplement || '',
        postal_code: property.postal_code,
        city: property.city,
        bag_pand_ids: property.bag_pand_ids || [],
        bag_vbo_ids: property.bag_vbo_ids || [],
        energy_labels: property.energy_labels || [],
        market_value: property.market_value,
        total_sqm_bvo: property.total_sqm_bvo,
        total_sqm_vvo: property.total_sqm_vvo,
        asset_class: property.asset_class,
        capex_percentage: property.capex_percentage || '',
        image_url: property.image_url || '',
        notes: property.notes || '',
        owners: property.owners || property.legal_owners || []
      });
    } else {
      setSelectedProperty(null);
      setPropertyForm(initialPropertyForm);
    }
    setNewBagPandId('');
    setNewBagVboId('');
    setPropertyDialogOpen(true);
  };

  // BAG ID validation - Dutch format: 16 digits
  const validateBagId = (id, type) => {
    const cleanId = id.trim();
    // Dutch BAG IDs are 16 digits
    if (!/^\d{16}$/.test(cleanId)) {
      toast.error(`Invalid BAG ${type} ID format. Must be 16 digits.`);
      return false;
    }
    return true;
  };

  // BAG Pand ID handlers
  const handleAddBagPandId = () => {
    if (!newBagPandId.trim()) return;
    if (!validateBagId(newBagPandId, 'Pand')) return;
    if (propertyForm.bag_pand_ids.includes(newBagPandId.trim())) {
      toast.error('This BAG Pand ID already exists');
      return;
    }
    setPropertyForm({
      ...propertyForm,
      bag_pand_ids: [...propertyForm.bag_pand_ids, newBagPandId.trim()]
    });
    setNewBagPandId('');
  };

  const handleRemoveBagPandId = (id) => {
    setPropertyForm({
      ...propertyForm,
      bag_pand_ids: propertyForm.bag_pand_ids.filter(pid => pid !== id)
    });
  };

  // BAG VBO ID handlers
  const handleAddBagVboId = () => {
    if (!newBagVboId.trim()) return;
    if (!validateBagId(newBagVboId, 'VBO')) return;
    if (propertyForm.bag_vbo_ids.includes(newBagVboId.trim())) {
      toast.error('This BAG VBO ID already exists');
      return;
    }
    setPropertyForm({
      ...propertyForm,
      bag_vbo_ids: [...propertyForm.bag_vbo_ids, newBagVboId.trim()]
    });
    setNewBagVboId('');
  };

  const handleRemoveBagVboId = (id) => {
    // Also remove any energy labels associated with this VBO ID
    setPropertyForm({
      ...propertyForm,
      bag_vbo_ids: propertyForm.bag_vbo_ids.filter(vid => vid !== id),
      energy_labels: propertyForm.energy_labels.filter(el => el.bag_vbo_id !== id)
    });
  };

  // Energy label handlers
  const handleAddEnergyLabel = () => {
    if (!energyLabelInput.bag_vbo_id) return;
    const exists = propertyForm.energy_labels.some(el => el.bag_vbo_id === energyLabelInput.bag_vbo_id);
    if (exists) {
      setPropertyForm({
        ...propertyForm,
        energy_labels: propertyForm.energy_labels.map(el => 
          el.bag_vbo_id === energyLabelInput.bag_vbo_id ? { ...energyLabelInput } : el
        )
      });
    } else {
      setPropertyForm({
        ...propertyForm,
        energy_labels: [...propertyForm.energy_labels, { ...energyLabelInput }]
      });
    }
    setEnergyLabelInput({ bag_vbo_id: '', energy_label: 'A' });
  };

  const handleRemoveEnergyLabel = (vboId) => {
    setPropertyForm({
      ...propertyForm,
      energy_labels: propertyForm.energy_labels.filter(el => el.bag_vbo_id !== vboId)
    });
  };

  // Legal owner handlers
  const handleAddOwner = () => {
    if (!ownerInput.party_id || !ownerInput.ownership_percentage) return;
    const exists = propertyForm.owners.some(lo => lo.party_id === ownerInput.party_id);
    if (exists) {
      setPropertyForm({
        ...propertyForm,
        owners: propertyForm.owners.map(lo => 
          lo.party_id === ownerInput.party_id ? { ...ownerInput, ownership_percentage: parseFloat(ownerInput.ownership_percentage) } : lo
        )
      });
    } else {
      setPropertyForm({
        ...propertyForm,
        owners: [...propertyForm.owners, { ...ownerInput, ownership_percentage: parseFloat(ownerInput.ownership_percentage) }]
      });
    }
    setOwnerInput({ party_id: '', ownership_percentage: '' });
  };

  const handleRemoveOwner = (partyId) => {
    setPropertyForm({
      ...propertyForm,
      owners: propertyForm.owners.filter(lo => lo.party_id !== partyId)
    });
  };

  const handlePropertySubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...propertyForm,
        market_value: parseFloat(propertyForm.market_value),
        total_sqm_bvo: parseFloat(propertyForm.total_sqm_bvo),
        total_sqm_vvo: parseFloat(propertyForm.total_sqm_vvo),
        capex_percentage: parseFloat(propertyForm.capex_percentage) || 0,
        address: `${propertyForm.street} ${propertyForm.house_number}${propertyForm.house_number_supplement ? ' ' + propertyForm.house_number_supplement : ''}`
      };

      if (selectedProperty) {
        await axios.put(`${API}/realestate/${selectedProperty.realestate_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/realestate`, payload, { withCredentials: true });
      }
      toast.success(t('savedSuccessfully'));
      setPropertyDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  // Tenant handlers
  const handleOpenTenantDialog = (tenant = null, propertyId = null) => {
    if (tenant) {
      setSelectedTenant(tenant);
      setTenantForm({
        realestate_id: tenant.realestate_id,
        bag_vbo_id: tenant.bag_vbo_id,
        tenant_name: tenant.tenant_name,
        chamber_of_commerce_number: tenant.chamber_of_commerce_number || '',
        start_date: tenant.start_date,
        end_date: tenant.end_date,
        option_periods: tenant.option_periods || '',
        rental_amount: tenant.rental_amount,
        incentives: tenant.incentives || '',
        sqm_rented: tenant.sqm_rented,
        is_vacancy: tenant.is_vacancy
      });
    } else {
      setSelectedTenant(null);
      setTenantForm({
        ...initialTenantForm,
        realestate_id: propertyId || viewingProperty?.realestate_id || ''
      });
    }
    setTenantDialogOpen(true);
  };

  const handleTenantSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...tenantForm,
        rental_amount: parseFloat(tenantForm.rental_amount),
        incentives: tenantForm.incentives ? parseFloat(tenantForm.incentives) : 0,
        sqm_rented: parseFloat(tenantForm.sqm_rented)
      };

      if (selectedTenant) {
        await axios.put(`${API}/tenants/${selectedTenant.tenant_id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/tenants`, payload, { withCredentials: true });
      }
      toast.success(t('savedSuccessfully'));
      setTenantDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    } finally {
      setSaving(false);
    }
  };

  // Contract extraction handler
  const handleContractExtract = async (file) => {
    if (!file) return;
    
    // Validate file type
    const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      toast.error('Alleen PDF en DOCX bestanden worden ondersteund');
      return;
    }
    
    setExtractingContract(true);
    
    try {
      // First, upload the file to get a URL
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);
      
      const uploadResponse = await axios.post(`${API}/contracts/upload`, uploadFormData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      let contractUrl = null;
      if (uploadResponse.data.success) {
        contractUrl = uploadResponse.data.file_url;
        toast.success('Contract geüpload');
      }
      
      // Then extract data from the file
      const extractFormData = new FormData();
      extractFormData.append('file', file);
      
      const response = await axios.post(`${API}/contracts/extract`, extractFormData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success && response.data.extracted_data) {
        const extracted = response.data.extracted_data;
        
        // Auto-fill tenant form with extracted data AND the uploaded contract URL
        setTenantForm(prev => ({
          ...prev,
          tenant_name: extracted.tenant_name || prev.tenant_name,
          rental_amount: extracted.rental_amount_annual ? String(extracted.rental_amount_annual) : prev.rental_amount,
          sqm_rented: extracted.sqm_rented ? String(extracted.sqm_rented) : prev.sqm_rented,
          start_date: extracted.start_date || prev.start_date,
          end_date: extracted.end_date || prev.end_date,
          option_periods: extracted.option_periods || prev.option_periods,
          contract_type: extracted.contract_type || prev.contract_type,
          contract_url: contractUrl || prev.contract_url  // Save the uploaded file URL
        }));
        
        toast.success(`Contract gegevens geëxtraheerd: ${extracted.contract_type || 'ROZ Contract'}`);
        
        if (extracted.tenant_name) {
          toast.info(`Huurder: ${extracted.tenant_name}`);
        }
      } else {
        // Even if extraction failed, still save the contract URL
        if (contractUrl) {
          setTenantForm(prev => ({
            ...prev,
            contract_url: contractUrl
          }));
        }
        toast.warning('Kon geen gestructureerde data extraheren, maar contract is wel geüpload');
      }
    } catch (error) {
      console.error('Contract extraction error:', error);
      toast.error(error.response?.data?.detail || 'Fout bij verwerken van contract');
    } finally {
      setExtractingContract(false);
      if (contractFileRef.current) {
        contractFileRef.current.value = '';
      }
    }
  };

  // Contract upload handler (save to server)
  const handleContractUpload = async (file, tenantId = null) => {
    if (!file) return null;
    
    setUploadingContract(true);
    const formData = new FormData();
    formData.append('file', file);
    if (tenantId) {
      formData.append('tenant_id', tenantId);
    }
    
    try {
      const response = await axios.post(`${API}/contracts/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data.success) {
        toast.success('Contract geüpload');
        return response.data.file_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fout bij uploaden van contract');
    } finally {
      setUploadingContract(false);
    }
    return null;
  };

  // Delete handlers
  const handleDelete = async () => {
    try {
      if (deleteType === 'property') {
        await axios.delete(`${API}/realestate/${selectedProperty.realestate_id}`, { withCredentials: true });
      } else {
        await axios.delete(`${API}/tenants/${selectedTenant.tenant_id}`, { withCredentials: true });
      }
      toast.success(t('deletedSuccessfully'));
      setDeleteDialogOpen(false);
      setSelectedProperty(null);
      setSelectedTenant(null);
      if (deleteType === 'property') setViewingProperty(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('errorOccurred'));
    }
  };

  const getPropertyTenants = (propertyId) => tenants.filter(t => t.realestate_id === propertyId);
  
  const getOwnerName = (partyId) => {
    const party = parties.find(p => p.party_id === partyId);
    return party?.name || partyId;
  };

  // Get VBO IDs that don't have an energy label yet
  const getAvailableVboIds = () => {
    const labeledVbos = propertyForm.energy_labels.map(el => el.bag_vbo_id);
    return propertyForm.bag_vbo_ids.filter(vbo => !labeledVbos.includes(vbo));
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64 bg-slate-800" />
          <Skeleton className="h-96 bg-slate-800" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="realestate-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white font-['Comfortaa']">{t('realEstate')}</h1>
            <p className="text-slate-400 mt-1">
              {properties.length} {properties.length === 1 ? 'property' : 'properties'} in portfolio
            </p>
          </div>
          <Button 
            className="bg-[#b4976f] hover:bg-[#a38663]"
            onClick={() => handleOpenPropertyDialog()}
            data-testid="add-property-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('addProperty')}
          </Button>
        </div>

        {/* Main Content with Tabs */}
        <Tabs defaultValue="properties" className="space-y-6">
          <TabsList className="bg-[#1e293b] border border-slate-700">
            <TabsTrigger value="properties" className="data-[state=active]:bg-[#b4976f] data-[state=active]:text-white" data-testid="properties-tab">
              <Building2 className="mr-2 h-4 w-4" />
              {t('realEstate')}
            </TabsTrigger>
            <TabsTrigger value="tenants" className="data-[state=active]:bg-[#b4976f] data-[state=active]:text-white" data-testid="tenants-tab">
              <Users className="mr-2 h-4 w-4" />
              {t('tenants')}
            </TabsTrigger>
          </TabsList>

          {/* Properties Tab */}
          <TabsContent value="properties" className="space-y-4">
            {viewingProperty ? (
              // Property Detail View
              <Card className="bg-[#1e293b] border-slate-700">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <Button variant="ghost" size="sm" onClick={() => setViewingProperty(null)} className="text-slate-400 mb-2">
                      ← Back to list
                    </Button>
                    <CardTitle className="text-xl text-white font-['Comfortaa'] flex items-center gap-2">
                      <Building2 className="h-5 w-5 text-[#b4976f]" />
                      {viewingProperty.street} {viewingProperty.house_number}
                    </CardTitle>
                    <p className="text-slate-400">{viewingProperty.postal_code} {viewingProperty.city}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleOpenPropertyDialog(viewingProperty)} className="border-slate-600">
                      <Edit className="h-4 w-4 mr-2" />
                      {t('edit')}
                    </Button>
                    <Button className="bg-[#b4976f] hover:bg-[#a38663]" size="sm" onClick={() => handleOpenTenantDialog(null, viewingProperty.realestate_id)} data-testid="add-tenant-btn">
                      <Plus className="h-4 w-4 mr-2" />
                      {t('addTenant')}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Property Details */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <div className="p-4 rounded-lg bg-slate-800/50">
                      <p className="text-sm text-slate-400">{t('marketValue')}</p>
                      <p className="text-lg font-semibold text-[#b4976f]">{formatEuro(viewingProperty.market_value)}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-800/50">
                      <p className="text-sm text-slate-400">{t('grossRentalIncome')}</p>
                      <p className="text-lg font-semibold text-white">{formatEuro(viewingProperty.gross_rental_income)}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-800/50">
                      <p className="text-sm text-slate-400">{t('netRentalIncome')}</p>
                      <p className="text-lg font-semibold text-white">{formatEuro(viewingProperty.net_rental_income)}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-800/50">
                      <p className="text-sm text-slate-400">{t('totalSqmVvo')}</p>
                      <p className="text-lg font-semibold text-white">{formatSqm(viewingProperty.total_sqm_vvo)}</p>
                    </div>
                    <div className="p-4 rounded-lg bg-slate-800/50">
                      <p className="text-sm text-slate-400">{t('energyLabel')}</p>
                      <span className="text-lg font-semibold px-2 py-1 rounded" style={{ 
                        backgroundColor: `${ENERGY_LABEL_COLORS[viewingProperty.best_energy_label] || '#64748b'}20`,
                        color: ENERGY_LABEL_COLORS[viewingProperty.best_energy_label] || '#64748b'
                      }}>
                        {viewingProperty.best_energy_label || 'N/A'}
                      </span>
                    </div>
                  </div>

                  {/* BAG IDs */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {viewingProperty.bag_pand_ids?.length > 0 && (
                      <div className="p-4 rounded-lg bg-slate-800/50">
                        <p className="text-sm text-slate-400 mb-2">{t('bagPandId')}</p>
                        <div className="flex flex-wrap gap-2">
                          {viewingProperty.bag_pand_ids.map((id, idx) => (
                            <span key={idx} className="px-2 py-1 rounded bg-slate-700 text-sm text-white">
                              {id}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {viewingProperty.bag_vbo_ids?.length > 0 && (
                      <div className="p-4 rounded-lg bg-slate-800/50">
                        <p className="text-sm text-slate-400 mb-2">{t('bagVboId')}</p>
                        <div className="flex flex-wrap gap-2">
                          {viewingProperty.bag_vbo_ids.map((id, idx) => (
                            <span key={idx} className="px-2 py-1 rounded bg-slate-700 text-sm text-white">
                              {id}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Owners */}
                  {(viewingProperty.owners?.length > 0 || viewingProperty.legal_owners?.length > 0) && (
                    <div className="p-4 rounded-lg bg-slate-800/50">
                      <p className="text-sm text-slate-400 mb-2">{t('parties')} (Eigenaren)</p>
                      <div className="flex flex-wrap gap-2">
                        {(viewingProperty.owners || viewingProperty.legal_owners || []).map((lo, idx) => (
                          <span key={idx} className="px-2 py-1 rounded bg-[#264162]/50 text-sm text-white">
                            {getOwnerName(lo.party_id || lo.owner_id)} ({lo.ownership_percentage}%)
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Energy Labels per VBO */}
                  {viewingProperty.energy_labels?.length > 0 && (
                    <div className="p-4 rounded-lg bg-slate-800/50">
                      <p className="text-sm text-slate-400 mb-2">{t('energyLabel')} per BAG VBO ID</p>
                      <div className="flex flex-wrap gap-2">
                        {viewingProperty.energy_labels.map((el, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-700 text-sm">
                            {el.bag_vbo_id}: 
                            <span style={{ color: ENERGY_LABEL_COLORS[el.energy_label] || '#64748b' }}>
                              {el.energy_label}
                            </span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Property Image */}
                  {viewingProperty.image_url && (
                    <div className="rounded-lg overflow-hidden border border-slate-700">
                      <img src={viewingProperty.image_url} alt={viewingProperty.address} className="w-full h-48 object-cover" />
                    </div>
                  )}

                  {/* Tenants Table */}
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-4">{t('tenants')}</h3>
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700">
                          <TableHead className="text-slate-400">{t('tenantName')}</TableHead>
                          <TableHead className="text-slate-400">BAG VBO ID</TableHead>
                          <TableHead className="text-slate-400 text-right">{t('rentalAmount')}</TableHead>
                          <TableHead className="text-slate-400 text-right">{t('sqmRented')}</TableHead>
                          <TableHead className="text-slate-400 text-right">{t('rentPerSqm')}</TableHead>
                          <TableHead className="text-slate-400">{t('endDate')}</TableHead>
                          <TableHead className="text-slate-400 text-center">Contract</TableHead>
                          <TableHead className="text-slate-400 text-right">{t('edit')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {getPropertyTenants(viewingProperty.realestate_id).length > 0 ? (
                          getPropertyTenants(viewingProperty.realestate_id).map((tenant) => (
                            <TableRow key={tenant.tenant_id} className="border-slate-700 table-row-hover">
                              <TableCell className="text-white">
                                {tenant.is_vacancy ? (
                                  <span className="text-amber-400 flex items-center gap-2">
                                    <span className="status-dot status-warning"></span>
                                    {t('vacancy')}
                                  </span>
                                ) : tenant.tenant_name}
                              </TableCell>
                              <TableCell className="text-slate-300">{tenant.bag_vbo_id}</TableCell>
                              <TableCell className="text-right text-[#c8b599]">{formatEuro(tenant.rental_amount)}</TableCell>
                              <TableCell className="text-right text-white">{formatSqm(tenant.sqm_rented)}</TableCell>
                              <TableCell className="text-right text-white">{formatEuro(tenant.rent_per_sqm)}/m²</TableCell>
                              <TableCell className="text-slate-300">{tenant.end_date}</TableCell>
                              <TableCell className="text-center">
                                {tenant.contract_url ? (
                                  <a 
                                    href={`${process.env.REACT_APP_BACKEND_URL}${tenant.contract_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-[#b4976f] hover:text-[#c8b599] text-sm"
                                    title={tenant.contract_type || 'Contract'}
                                  >
                                    <FileText className="h-4 w-4" />
                                  </a>
                                ) : (
                                  <span className="text-slate-500">-</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2">
                                  <Button variant="ghost" size="sm" onClick={() => handleOpenTenantDialog(tenant)}>
                                    <Edit className="h-4 w-4 text-slate-400 hover:text-white" />
                                  </Button>
                                  {canDelete() && (
                                    <Button variant="ghost" size="sm" onClick={() => {
                                      setSelectedTenant(tenant);
                                      setDeleteType('tenant');
                                      setDeleteDialogOpen(true);
                                    }}>
                                      <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-400" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center text-slate-500 py-8">{t('noData')}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            ) : (
              // Properties List
              <Card className="bg-[#1e293b] border-slate-700">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700 hover:bg-transparent">
                        <TableHead className="text-slate-400">{t('address')}</TableHead>
                        <TableHead className="text-slate-400">{t('city')}</TableHead>
                        <TableHead className="text-slate-400">{t('energyLabel')}</TableHead>
                        <TableHead className="text-slate-400 text-right">{t('marketValue')}</TableHead>
                        <TableHead className="text-slate-400 text-right">{t('grossRentalIncome')}</TableHead>
                        <TableHead className="text-slate-400 text-right">{t('vacancyRate')}</TableHead>
                        <TableHead className="text-slate-400 text-right">{t('edit')}</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {properties.length > 0 ? (
                        properties.map((property) => {
                          const sqmRented = property.sqm_rented || 0;
                          const sqmVacant = property.sqm_vacant || 0;
                          const totalSqm = sqmRented + sqmVacant;
                          const vacancyPct = totalSqm > 0 ? (sqmVacant / totalSqm * 100) : 0;
                          
                          return (
                            <TableRow 
                              key={property.realestate_id} 
                              className="border-slate-700 table-row-hover cursor-pointer"
                              onClick={() => setViewingProperty(property)}
                            >
                              <TableCell className="text-white font-medium">
                                <div className="flex items-center gap-2">
                                  <Building2 className="h-4 w-4 text-[#b4976f]" />
                                  {property.street} {property.house_number}
                                </div>
                              </TableCell>
                              <TableCell className="text-slate-300">{property.city}</TableCell>
                              <TableCell>
                                <span 
                                  className="px-2 py-1 rounded text-xs font-medium"
                                  style={{ 
                                    backgroundColor: `${ENERGY_LABEL_COLORS[property.best_energy_label] || '#64748b'}20`,
                                    color: ENERGY_LABEL_COLORS[property.best_energy_label] || '#64748b'
                                  }}
                                >
                                  {property.best_energy_label || 'N/A'}
                                </span>
                              </TableCell>
                              <TableCell className="text-right text-[#c8b599] euro-amount">
                                {formatEuro(property.market_value)}
                              </TableCell>
                              <TableCell className="text-right text-white euro-amount">
                                {formatEuro(property.gross_rental_income)}
                              </TableCell>
                              <TableCell className="text-right">
                                <span className={vacancyPct > 10 ? 'text-amber-400' : 'text-white'}>
                                  {vacancyPct.toFixed(1)}%
                                </span>
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                  <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    onClick={() => handleOpenBuildingDetail(property)}
                                    title="VT15 Gebouwenpaspoort"
                                    data-testid={`view-building-detail-${property.realestate_id}`}
                                  >
                                    <Eye className="h-4 w-4 text-[#b4976f] hover:text-[#c8b599]" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => setViewingProperty(property)}>
                                    <ChevronRight className="h-4 w-4 text-slate-400 hover:text-white" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => handleOpenPropertyDialog(property)}>
                                    <Edit className="h-4 w-4 text-slate-400 hover:text-white" />
                                  </Button>
                                  {canDelete() && (
                                    <Button variant="ghost" size="sm" onClick={() => {
                                      setSelectedProperty(property);
                                      setDeleteType('property');
                                      setDeleteDialogOpen(true);
                                    }}>
                                      <Trash2 className="h-4 w-4 text-slate-400 hover:text-red-400" />
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-slate-500 py-12">
                            <Building2 className="h-12 w-12 mx-auto mb-4 text-slate-600" />
                            <p>{t('noData')}</p>
                            <Button variant="link" className="text-[#b4976f] mt-2" onClick={() => handleOpenPropertyDialog()}>
                              {t('addProperty')}
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* All Tenants Tab */}
          <TabsContent value="tenants">
            <Card className="bg-[#1e293b] border-slate-700">
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700 hover:bg-transparent">
                      <TableHead className="text-slate-400">{t('tenantName')}</TableHead>
                      <TableHead className="text-slate-400">{t('address')}</TableHead>
                      <TableHead className="text-slate-400 text-right">{t('rentalAmount')}</TableHead>
                      <TableHead className="text-slate-400 text-right">{t('sqmRented')}</TableHead>
                      <TableHead className="text-slate-400 text-right">{t('rentPerSqm')}</TableHead>
                      <TableHead className="text-slate-400">{t('startDate')}</TableHead>
                      <TableHead className="text-slate-400">{t('endDate')}</TableHead>
                      <TableHead className="text-slate-400 text-center">Contract</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tenants.filter(t => !t.is_vacancy).length > 0 ? (
                      tenants.filter(t => !t.is_vacancy).map((tenant) => {
                        const property = properties.find(p => p.realestate_id === tenant.realestate_id);
                        const displayName = tenant.tenant_name || tenant.tenant_naam || 'Onbekend';
                        return (
                          <TableRow key={tenant.tenant_id} className="border-slate-700 table-row-hover">
                            <TableCell className="text-white font-medium">
                              <div className="flex items-center gap-2">
                                {displayName}
                              </div>
                            </TableCell>
                            <TableCell className="text-slate-300">
                              {property ? `${property.street} ${property.house_number}` : '-'}
                            </TableCell>
                            <TableCell className="text-right text-[#c8b599]">
                              {tenant.rental_amount ? formatEuro(tenant.rental_amount) : '-'}
                            </TableCell>
                            <TableCell className="text-right text-white">
                              {tenant.sqm_rented ? formatSqm(tenant.sqm_rented) : '-'}
                            </TableCell>
                            <TableCell className="text-right text-white">
                              {tenant.rent_per_sqm ? `${formatEuro(tenant.rent_per_sqm)}/m²` : '-'}
                            </TableCell>
                            <TableCell className="text-slate-300">{tenant.start_date || '-'}</TableCell>
                            <TableCell className="text-slate-300">{tenant.end_date || '-'}</TableCell>
                            <TableCell className="text-center">
                              {tenant.contract_url ? (
                                <a 
                                  href={`${process.env.REACT_APP_BACKEND_URL}${tenant.contract_url}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-[#b4976f] hover:text-[#c8b599] text-sm"
                                  title={tenant.contract_type || 'Contract'}
                                >
                                  <FileText className="h-4 w-4" />
                                  {tenant.contract_type ? tenant.contract_type.replace('ROZ ', '') : 'PDF'}
                                </a>
                              ) : tenant.tenant_type ? (
                                <span className="text-slate-400 text-sm">{tenant.tenant_type}</span>
                              ) : (
                                <span className="text-slate-500">-</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center text-slate-500 py-12">
                          <Users className="h-12 w-12 mx-auto mb-4 text-slate-600" />
                          <p>{t('noData')}</p>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Property Dialog */}
        <Dialog open={propertyDialogOpen} onOpenChange={setPropertyDialogOpen}>
          <DialogContent className="bg-[#1e293b] border-slate-700 text-white max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-['Comfortaa']">
                {selectedProperty ? t('editProperty') : t('addProperty')}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handlePropertySubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('street')}</Label>
                  <Input value={propertyForm.street} onChange={(e) => setPropertyForm({ ...propertyForm, street: e.target.value })} className="bg-slate-900 border-slate-700 text-white" required data-testid="street-input" />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-2">
                    <Label className="text-slate-300">{t('houseNumber')}</Label>
                    <Input value={propertyForm.house_number} onChange={(e) => setPropertyForm({ ...propertyForm, house_number: e.target.value })} className="bg-slate-900 border-slate-700 text-white" required data-testid="house-number-input" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-300">Toev.</Label>
                    <Input value={propertyForm.house_number_supplement} onChange={(e) => setPropertyForm({ ...propertyForm, house_number_supplement: e.target.value })} className="bg-slate-900 border-slate-700 text-white" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('postalCode')}</Label>
                  <Input value={propertyForm.postal_code} onChange={(e) => setPropertyForm({ ...propertyForm, postal_code: e.target.value })} className="bg-slate-900 border-slate-700 text-white" required data-testid="postal-code-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('city')}</Label>
                  <Input value={propertyForm.city} onChange={(e) => setPropertyForm({ ...propertyForm, city: e.target.value })} className="bg-slate-900 border-slate-700 text-white" required data-testid="city-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('marketValue')} (€)</Label>
                  <Input type="number" value={propertyForm.market_value} onChange={(e) => setPropertyForm({ ...propertyForm, market_value: e.target.value })} className="bg-slate-900 border-slate-700 text-white" required min="0" step="0.01" data-testid="market-value-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('assetClass')}</Label>
                  <Select value={propertyForm.asset_class} onValueChange={(value) => setPropertyForm({ ...propertyForm, asset_class: value })}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="asset-class-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      {assetClasses.map((ac) => (
                        <SelectItem key={ac.value} value={ac.value} className="text-white hover:bg-slate-800">{t(ac.label)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('totalSqmBvo')}</Label>
                  <Input type="number" value={propertyForm.total_sqm_bvo} onChange={(e) => setPropertyForm({ ...propertyForm, total_sqm_bvo: e.target.value })} className="bg-slate-900 border-slate-700 text-white" required min="0" step="0.01" data-testid="sqm-bvo-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('totalSqmVvo')}</Label>
                  <Input type="number" value={propertyForm.total_sqm_vvo} onChange={(e) => setPropertyForm({ ...propertyForm, total_sqm_vvo: e.target.value })} className="bg-slate-900 border-slate-700 text-white" required min="0" step="0.01" data-testid="sqm-vvo-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('capexPercentage')} (%)</Label>
                  <Input type="number" value={propertyForm.capex_percentage} onChange={(e) => setPropertyForm({ ...propertyForm, capex_percentage: e.target.value })} className="bg-slate-900 border-slate-700 text-white" min="0" max="100" step="0.01" />
                </div>
              </div>

              {/* BAG Pand IDs Section */}
              <div className="space-y-2 p-4 rounded-lg bg-slate-800/50">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">{t('bagPandId')}</Label>
                  <BagLookupButton />
                </div>
                <div className="flex gap-2">
                  <Input 
                    value={newBagPandId} 
                    onChange={(e) => setNewBagPandId(e.target.value)} 
                    className="bg-slate-900 border-slate-700 text-white flex-1" 
                    placeholder="Enter BAG Pand ID (16 digits)"
                    onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddBagPandId(); } }}
                    data-testid="bag-pand-id-input"
                  />
                  <Button type="button" onClick={handleAddBagPandId} className="bg-[#264162] hover:bg-[#1e344f]">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {propertyForm.bag_pand_ids.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {propertyForm.bag_pand_ids.map((id, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-700 text-sm text-white">
                        {id}
                        <button type="button" onClick={() => handleRemoveBagPandId(id)} className="text-red-400 hover:text-red-300 ml-1">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* BAG VBO IDs Section */}
              <div className="space-y-2 p-4 rounded-lg bg-slate-800/50">
                <div className="flex items-center justify-between">
                  <Label className="text-slate-300">{t('bagVboId')}</Label>
                  <BagLookupButton />
                </div>
                <div className="flex gap-2">
                  <Input 
                    value={newBagVboId} 
                    onChange={(e) => setNewBagVboId(e.target.value)} 
                    className="bg-slate-900 border-slate-700 text-white flex-1" 
                    placeholder="Enter BAG VBO ID"
                    onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddBagVboId(); } }}
                    data-testid="bag-vbo-id-input"
                  />
                  <Button type="button" onClick={handleAddBagVboId} className="bg-[#264162] hover:bg-[#1e344f]">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {propertyForm.bag_vbo_ids.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {propertyForm.bag_vbo_ids.map((id, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-700 text-sm text-white">
                        {id}
                        <button type="button" onClick={() => handleRemoveBagVboId(id)} className="text-red-400 hover:text-red-300 ml-1">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Energy Labels Section */}
              <div className="space-y-2 p-4 rounded-lg bg-slate-800/50">
                <Label className="text-slate-300 flex items-center gap-2">
                  <Leaf className="h-4 w-4 text-emerald-400" />
                  {t('energyLabel')} per BAG VBO ID
                </Label>
                
                {/* Auto-fetch energy label button */}
                <div className="flex items-center gap-2 mb-3">
                  <Button
                    type="button"
                    onClick={fetchEnergyLabels}
                    disabled={fetchingEnergyLabel}
                    className="bg-emerald-700 hover:bg-emerald-600 text-white"
                    data-testid="fetch-energy-label-btn"
                  >
                    {fetchingEnergyLabel ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Zap className="h-4 w-4 mr-2" />
                    )}
                    {fetchingEnergyLabel ? 'Ophalen...' : 'Energielabel Ophalen (EP-Online)'}
                  </Button>
                  <span className="text-xs text-slate-500">
                    Zoekt op: BAG Pand ID → BAG VBO ID → Postcode+Huisnummer
                  </span>
                </div>

                {/* Energy label results from API */}
                {energyLabelResults.length > 0 && (
                  <div className="p-3 rounded bg-emerald-900/30 border border-emerald-700 mb-3">
                    <p className="text-sm text-emerald-300 mb-2">Gevonden energielabels - klik om toe te passen:</p>
                    <div className="flex flex-wrap gap-2">
                      {energyLabelResults.map((label, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => applyEnergyLabel(label)}
                          className="inline-flex items-center gap-2 px-3 py-2 rounded bg-slate-800 hover:bg-slate-700 border border-slate-600 text-sm transition-colors"
                        >
                          <span style={{ color: ENERGY_LABEL_COLORS[label.energielabel_klasse] || '#64748b', fontWeight: 'bold' }}>
                            {label.energielabel_klasse}
                          </span>
                          <span className="text-slate-400">|</span>
                          <span className="text-slate-300">{label.adres || label.bag_vbo_id}</span>
                          {label.energielabel_registratiedatum && (
                            <span className="text-slate-500 text-xs">({label.energielabel_registratiedatum})</span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Manual energy label entry */}
                {propertyForm.bag_vbo_ids.length === 0 ? (
                  <p className="text-sm text-slate-500">Add BAG VBO IDs above first to assign energy labels</p>
                ) : (
                  <>
                    <div className="flex gap-2">
                      <Select value={energyLabelInput.bag_vbo_id} onValueChange={(value) => setEnergyLabelInput({ ...energyLabelInput, bag_vbo_id: value })}>
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white flex-1" data-testid="energy-vbo-select">
                          <SelectValue placeholder="Select BAG VBO ID" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          {propertyForm.bag_vbo_ids.map((vboId) => (
                            <SelectItem key={vboId} value={vboId} className="text-white hover:bg-slate-800">
                              {vboId}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select value={energyLabelInput.energy_label} onValueChange={(value) => setEnergyLabelInput({ ...energyLabelInput, energy_label: value })}>
                        <SelectTrigger className="bg-slate-900 border-slate-700 text-white w-24" data-testid="energy-label-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-900 border-slate-700">
                          {ENERGY_LABELS.map((label) => (
                            <SelectItem key={label} value={label} className="text-white hover:bg-slate-800">
                              <span style={{ color: ENERGY_LABEL_COLORS[label] }}>{label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Button type="button" onClick={handleAddEnergyLabel} className="bg-[#264162] hover:bg-[#1e344f]" disabled={!energyLabelInput.bag_vbo_id}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    {propertyForm.energy_labels.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {propertyForm.energy_labels.map((el, idx) => (
                          <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-700 text-sm">
                            {el.bag_vbo_id}: <span style={{ color: ENERGY_LABEL_COLORS[el.energy_label] }}>{el.energy_label}</span>
                            <button type="button" onClick={() => handleRemoveEnergyLabel(el.bag_vbo_id)} className="text-red-400 hover:text-red-300">
                              <X className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Owners Section */}
              <div className="space-y-2 p-4 rounded-lg bg-slate-800/50">
                <Label className="text-slate-300">{t('parties')} (Eigenaren)</Label>
                <div className="flex gap-2">
                  <Select value={ownerInput.party_id} onValueChange={(value) => setOwnerInput({ ...ownerInput, party_id: value })}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white flex-1">
                      <SelectValue placeholder={t('selectOwner')} />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      {parties.map((party) => (
                        <SelectItem key={party.party_id} value={party.party_id} className="text-white hover:bg-slate-800">
                          {party.name} ({party.party_type === 'bv' ? 'B.V.' : 'Particulier'})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input type="number" value={ownerInput.ownership_percentage} onChange={(e) => setOwnerInput({ ...ownerInput, ownership_percentage: e.target.value })} className="bg-slate-900 border-slate-700 text-white w-24" placeholder="%" min="0" max="100" />
                  <Button type="button" onClick={handleAddOwner} className="bg-[#264162] hover:bg-[#1e344f]">
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>
                {propertyForm.owners.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {propertyForm.owners.map((lo, idx) => (
                      <span key={idx} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-700 text-sm">
                        {getOwnerName(lo.party_id)}: <span className="text-[#b4976f]">{lo.ownership_percentage}%</span>
                        <button type="button" onClick={() => handleRemoveOwner(lo.party_id)} className="text-red-400 hover:text-red-300">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-slate-300">{t('image')} URL</Label>
                <Input value={propertyForm.image_url} onChange={(e) => setPropertyForm({ ...propertyForm, image_url: e.target.value })} className="bg-slate-900 border-slate-700 text-white" placeholder="https://..." />
              </div>
              <div className="space-y-2">
                <Label className="text-slate-300">{t('notes')}</Label>
                <Textarea value={propertyForm.notes} onChange={(e) => setPropertyForm({ ...propertyForm, notes: e.target.value })} className="bg-slate-900 border-slate-700 text-white" rows={3} />
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setPropertyDialogOpen(false)} className="border-slate-600 text-slate-300">{t('cancel')}</Button>
                <Button type="submit" className="bg-[#b4976f] hover:bg-[#a38663]" disabled={saving} data-testid="save-property-btn">
                  {saving ? t('loading') : t('save')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Tenant Dialog */}
        <Dialog open={tenantDialogOpen} onOpenChange={setTenantDialogOpen}>
          <DialogContent className="bg-[#1e293b] border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-xl font-['Comfortaa']">
                {selectedTenant ? t('editTenant') : t('addTenant')}
              </DialogTitle>
              <DialogDescription className="text-slate-400">
                Upload een ROZ huurcontract om automatisch gegevens te extraheren
              </DialogDescription>
            </DialogHeader>
            
            {/* Contract Upload Section */}
            {!selectedTenant && (
              <div className="p-4 rounded-lg bg-slate-800/50 border border-dashed border-slate-600">
                <div className="flex items-center gap-3 mb-3">
                  <FileText className="h-5 w-5 text-[#b4976f]" />
                  <Label className="text-slate-300 font-medium">ROZ Huurcontract uploaden (optioneel)</Label>
                </div>
                <div className="flex gap-2">
                  <input
                    ref={contractFileRef}
                    type="file"
                    accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleContractExtract(file);
                    }}
                    className="hidden"
                    id="contract-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                    onClick={() => contractFileRef.current?.click()}
                    disabled={extractingContract}
                  >
                    {extractingContract ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Extracting...
                      </>
                    ) : (
                      <>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload PDF/DOCX
                      </>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Ondersteund: ROZ Kantoorruimte, ROZ Winkelruimte (290 BW)
                </p>
              </div>
            )}
            
            <form onSubmit={handleTenantSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('tenantName')}</Label>
                  <Input value={tenantForm.tenant_name} onChange={(e) => setTenantForm({ ...tenantForm, tenant_name: e.target.value })} className="bg-slate-900 border-slate-700 text-white" required data-testid="tenant-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('bagVboId')}</Label>
                  {viewingProperty?.bag_vbo_ids?.length > 0 ? (
                    <Select value={tenantForm.bag_vbo_id} onValueChange={(value) => setTenantForm({ ...tenantForm, bag_vbo_id: value })}>
                      <SelectTrigger className="bg-slate-900 border-slate-700 text-white" data-testid="tenant-vbo-select">
                        <SelectValue placeholder="Select BAG VBO ID" />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-900 border-slate-700">
                        {viewingProperty.bag_vbo_ids.map((vboId) => (
                          <SelectItem key={vboId} value={vboId} className="text-white hover:bg-slate-800">
                            {vboId}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={tenantForm.bag_vbo_id} onChange={(e) => setTenantForm({ ...tenantForm, bag_vbo_id: e.target.value })} className="bg-slate-900 border-slate-700 text-white" required data-testid="tenant-vbo-input" />
                  )}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('chamberOfCommerce')}</Label>
                  <Input value={tenantForm.chamber_of_commerce_number} onChange={(e) => setTenantForm({ ...tenantForm, chamber_of_commerce_number: e.target.value })} className="bg-slate-900 border-slate-700 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('rentalAmount')} (€)</Label>
                  <Input type="number" value={tenantForm.rental_amount} onChange={(e) => setTenantForm({ ...tenantForm, rental_amount: e.target.value })} className="bg-slate-900 border-slate-700 text-white" required min="0" step="0.01" data-testid="rental-amount-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('sqmRented')}</Label>
                  <Input type="number" value={tenantForm.sqm_rented} onChange={(e) => setTenantForm({ ...tenantForm, sqm_rented: e.target.value })} className="bg-slate-900 border-slate-700 text-white" required min="0" step="0.01" data-testid="sqm-rented-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('incentives')} (€)</Label>
                  <Input type="number" value={tenantForm.incentives} onChange={(e) => setTenantForm({ ...tenantForm, incentives: e.target.value })} className="bg-slate-900 border-slate-700 text-white" min="0" step="0.01" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('startDate')}</Label>
                  <Input type="date" value={tenantForm.start_date} onChange={(e) => setTenantForm({ ...tenantForm, start_date: e.target.value })} className="bg-slate-900 border-slate-700 text-white" required data-testid="start-date-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">{t('endDate')}</Label>
                  <Input type="date" value={tenantForm.end_date} onChange={(e) => setTenantForm({ ...tenantForm, end_date: e.target.value })} className="bg-slate-900 border-slate-700 text-white" required data-testid="end-date-input" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label className="text-slate-300">{t('optionPeriods')}</Label>
                  <Input value={tenantForm.option_periods} onChange={(e) => setTenantForm({ ...tenantForm, option_periods: e.target.value })} className="bg-slate-900 border-slate-700 text-white" placeholder="e.g., 2x5 years" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Contract Type</Label>
                  <Select value={tenantForm.contract_type || ''} onValueChange={(value) => setTenantForm({ ...tenantForm, contract_type: value })}>
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                      <SelectValue placeholder="Selecteer type" />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-900 border-slate-700">
                      <SelectItem value="ROZ Kantoorruimte" className="text-white hover:bg-slate-800">ROZ Kantoorruimte (Office)</SelectItem>
                      <SelectItem value="ROZ Winkelruimte" className="text-white hover:bg-slate-800">ROZ Winkelruimte (290 BW)</SelectItem>
                      <SelectItem value="Other" className="text-white hover:bg-slate-800">Anders</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-3">
                  <input type="checkbox" id="is_vacancy" checked={tenantForm.is_vacancy} onChange={(e) => setTenantForm({ ...tenantForm, is_vacancy: e.target.checked })} className="rounded border-slate-700 bg-slate-900 text-[#b4976f]" data-testid="is-vacancy-checkbox" />
                  <Label htmlFor="is_vacancy" className="text-slate-300">{t('vacancy')} (not counted as real income)</Label>
                </div>
                {/* Show existing contract if available */}
                {tenantForm.contract_url && (
                  <div className="md:col-span-2 p-3 rounded-lg bg-slate-800/50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-[#b4976f]" />
                      <span className="text-sm text-slate-300">Contract bijgevoegd</span>
                    </div>
                    <a 
                      href={`${process.env.REACT_APP_BACKEND_URL}${tenantForm.contract_url}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#b4976f] text-sm hover:underline"
                    >
                      Bekijken
                    </a>
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setTenantDialogOpen(false)} className="border-slate-600 text-slate-300">{t('cancel')}</Button>
                <Button type="submit" className="bg-[#b4976f] hover:bg-[#a38663]" disabled={saving} data-testid="save-tenant-btn">
                  {saving ? t('loading') : t('save')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-[#1e293b] border-slate-700">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-400" />
                {t('confirmDelete')}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-slate-400">
                {deleteType === 'property' 
                  ? `This will permanently delete "${selectedProperty?.street} ${selectedProperty?.house_number}" and all associated tenants.`
                  : `This will permanently delete tenant "${selectedTenant?.tenant_name}".`
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-slate-600 text-slate-300 hover:bg-slate-800">{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">{t('delete')}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* VT15 Building Detail Dialog (Gebouwenpaspoort) */}
        <BuildingDetailDialog
          building={selectedBuildingForDetail}
          isOpen={buildingDetailOpen}
          onClose={() => {
            setBuildingDetailOpen(false);
            setSelectedBuildingForDetail(null);
          }}
          onSave={handleBuildingDetailSave}
          t={t}
        />
      </div>
    </Layout>
  );
};
